import type { Range } from 'slate'

import { withoutLosingSelection } from '~publish/legacy/editor/helpers/withoutLosingSelection'

import type { BufferEditor } from '../../../BufferEditor/types.plate'
import type { WithPosition } from '../../../helpers/WithPosition'
import { findCharacterLimitPoint } from '../../character-limit-highlight'
import { LinkedInAnnotationInputElement } from '../nodes/LinkedInAnnotationInputElement'
import type { LinkedInAnnotationEntityDetails } from '../nodes/LinkedInAnnotationInputElement'

/**
 * Given an editor and a linkedin mention entity,
 * find its location in the editor and insert
 * a LinkedInAnnotationInputElement
 */
export const insertLinkedInMention = (
  editor: BufferEditor,
  data: WithPosition<LinkedInAnnotationEntityDetails>,
): Range | undefined => {
  const { start, length, ...mention } = data
  const anchor = findCharacterLimitPoint(editor, start)
  const focus =
    anchor &&
    editor.api.after(anchor, {
      distance: length,
    })

  if (!anchor || !focus) {
    return
  }

  const { localizedName } = mention

  // Only insert mention if text at saved indices matches expected mention text
  // NOTE - This will result in the mention not being picked up at save time
  // so it will essentially be forgotten.
  // Slack conversation: https://buffer.slack.com/archives/C05N3DJ5K7G/p1712242666626179
  const textAtIndices = editor.api.string({ anchor, focus })

  if (textAtIndices === localizedName) {
    const element = LinkedInAnnotationInputElement.new(mention)

    withoutLosingSelection(editor, () => {
      // insertNodes at a (non-collapsed) range deletes the range first,
      // then inserts — wrapNodes would instead make the existing text this
      // element's own children, which a void node can't hold.
      editor.tf.insertNodes(element, {
        at: { anchor, focus },
      })
    })
  }
}
